import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { ChevronLeft, AlertCircle, Check, Home, Users } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'

interface PlanDetails {
  name: string
  monthlyPrice: number
  yearlyPrice: number
  features: string[]
}

export default function PaymentPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  
  const [loading, setLoading] = useState(false)
  const [agreedToPayPalWarning, setAgreedToPayPalWarning] = useState(false)
  const [csrfToken, setCsrfToken] = useState<string>('')

  const state = location.state as any
  const isCommunityPayment = state?.isCommunityPayment || false
  const communityId = state?.communityId || null
  const communityName = state?.communityName || ''

  const planKey = (state?.plan as 'starter' | 'pro' | 'enterprise' | 'community') || 'starter'
  const billingCycle = (state?.billingCycle as 'monthly' | 'yearly') || 'monthly'
  const userEmail = state?.email || user?.email || ''
  
  const promoApplied = state?.promoApplied || false
  const promoCode = state?.promoCode || ''
  const promoDiscount = state?.promoDiscount || 0
  const discountedPrice = state?.price || 0

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${API_URL}/csrf-token`, {
          method: 'GET', credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        setCsrfToken(data.csrfToken)
        localStorage.setItem('csrfToken', data.csrfToken)
      } catch (error) {
        console.error('CSRF fetch failed:', error)
      }
    }
    fetchCsrfToken()
  }, [])

  const plans: Record<string, PlanDetails> = {
    starter: {
      name: 'Starter',
      monthlyPrice: 29, yearlyPrice: 21,
      features: ['Up to 1,000 subscribers', 'AI email generation', '5 Templates', '1 Team member', 'Basic analytics']
    },
    pro: {
      name: 'Professional',
      monthlyPrice: 99, yearlyPrice: 79,
      features: ['Up to 10,000 subscribers', 'Advanced AI generation', '20 Templates', '5 Team members', 'Advanced analytics', 'A/B testing']
    },
    enterprise: {
      name: 'Enterprise',
      monthlyPrice: 299, yearlyPrice: 249,
      features: ['Unlimited subscribers', 'Full AI with learning', 'Unlimited templates', 'Unlimited team', 'Enterprise analytics', '24/7 support']
    },
    community: {
      name: communityName || 'Community',
      monthlyPrice: state?.amount || 0, yearlyPrice: state?.amount || 0,
      features: ['Community access', 'Posts & comments', 'Polls & events', 'Member features']
    }
  }

  const planDetails = plans[planKey] || plans.starter
  const originalPrice = billingCycle === 'monthly' ? planDetails.monthlyPrice : planDetails.yearlyPrice
  const finalPrice = promoApplied ? discountedPrice : originalPrice
  const yearlySavings = planDetails.monthlyPrice * 12 - planDetails.yearlyPrice * 12
  const savingsPercent = Math.round((1 - planDetails.yearlyPrice / planDetails.monthlyPrice) * 100)

const handleCreatePlanOrder = async () => {
    try {
      setLoading(true)
      if (!userEmail) { toast.error('Email is required'); setLoading(false); return }
      if (!agreedToPayPalWarning) { toast.error('Please confirm your PayPal email'); setLoading(false); return }

      const token = localStorage.getItem('token') || localStorage.getItem('accessToken')
      if (!token) { toast.error('Please login again'); navigate('/signin'); return }

      // ✅ COMMUNITY PAYMENT - Use same PayPal flow as plans
      if (isCommunityPayment && communityId) {
        const response = await fetch(`${API_URL}/plans/create-order`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            email: userEmail,
            plan: 'community',
            billingCycle: 'monthly',
            amount: finalPrice,
            isCommunityPayment: true,
            communityId,
            communityName
          })
        })

        if (!response.ok) {
          if (response.status === 401) { toast.error('Session expired'); navigate('/signin'); return }
          throw new Error('Payment failed')
        }

        const data = await response.json()
        if (data.success && data.data?.approveUrl) {
          toast.success('Redirecting to PayPal...')
          setTimeout(() => { window.location.href = data.data.approveUrl }, 1500)
        } else {
          toast.error(data.message || 'Failed to create order')
        }
        return
      }

      // ✅ REGULAR PLAN PAYMENT (unchanged)
      const response = await fetch(`${API_URL}/plans/create-order`, {
        method: 'POST', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          email: userEmail,
          plan: planKey,
          billingCycle,
          amount: finalPrice,
          promoCode: promoApplied ? promoCode : null,
          promoDiscount: promoApplied ? promoDiscount : 0
        })
      })

      if (!response.ok) {
        if (response.status === 401) { toast.error('Session expired'); navigate('/signin'); return }
        throw new Error('Payment failed')
      }

      const data = await response.json()
      if (data.success && data.data?.approveUrl) {
        toast.success('Redirecting to PayPal...')
        setTimeout(() => { window.location.href = data.data.approveUrl }, 1500)
      } else {
        toast.error(data.message || 'Failed to create order')
      }
    } catch (error: any) {
      toast.error(error.message || 'Payment failed')
    } finally { setLoading(false) }
    
  }
  
  const handleBack = () => {
    if (isCommunityPayment) navigate(-1)
    else navigate('/plan-selection', { state: { email: userEmail } })
  } 
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={handleBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-8">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {isCommunityPayment ? `Join ${communityName}` : 'Complete Your Payment'}
          </h1>
          <p className="text-xl text-gray-600">Secure payment powered by PayPal</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-8">
            <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center text-lg">
                <span>{planDetails.name} {isCommunityPayment ? 'Community' : 'Plan'}</span>
                <span className="font-semibold">${finalPrice}/mo</span>
              </div>
              <div className="flex justify-between items-center text-sm text-indigo-100">
                <span>Billing Cycle</span>
                <span className="capitalize">{billingCycle}</span>
              </div>
              {billingCycle === 'yearly' && (
                <div className="bg-green-500 bg-opacity-30 rounded-lg p-3">
                  <Check className="w-5 h-5 text-green-200 inline mr-2" />
                  <span className="font-semibold">Save {savingsPercent}% (${yearlySavings}/year)</span>
                </div>
              )}
              <div className="border-t border-indigo-400 pt-4 mt-4">
                <div className="flex justify-between items-center text-2xl font-bold mt-2">
                  <span>Due Today</span>
                  <span>${billingCycle === 'yearly' ? finalPrice * 12 : finalPrice}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-yellow-900 mb-2">PayPal Email</h3>
                  <p className="text-sm text-yellow-800">Your PayPal email must match: <strong>{userEmail}</strong></p>
                </div>
              </div>
            </div>

            <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={agreedToPayPalWarning} onChange={(e) => setAgreedToPayPalWarning(e.target.checked)} className="h-4 w-4 text-indigo-600 rounded mt-1" />
                <span className="text-sm text-gray-700">
                  <strong>I confirm</strong> my PayPal email matches <strong>{userEmail}</strong>
                </span>
              </label>
            </div>

            <div className="space-y-3">
              <button onClick={handleCreatePlanOrder} disabled={loading || !agreedToPayPalWarning}
                className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-4 rounded-lg font-bold hover:from-indigo-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg">
                {loading ? 'Processing...' : `Pay $${billingCycle === 'yearly' ? finalPrice * 12 : finalPrice} with PayPal`}
              </button>
              <button onClick={handleBack} disabled={loading}
                className="w-full bg-white text-gray-700 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50">
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}